import { FC, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import ReplayIcon from '@mui/icons-material/Replay'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'

import {
  QueueJobState,
  useQueuesOverviewQuery,
  useQueueJobsLazyQuery,
  usePauseQueueMutation,
  useResumeQueueMutation,
  useRetryQueueJobMutation,
  useRemoveQueueJobMutation
} from '../../../../generated'
import { ConfirmationModal } from '../../../components/common/ConfirmationModal'
import { Alert } from '../../../components/common/Alert'
import { PaginationWithJump } from '../../../components/common/PaginationWithJump'

const REFRESH_INTERVAL_MS = 5000
const JOB_PAGE_SIZE = 20

// The job states we surface as tabs in the drill-down, in the order most useful during an incident.
const JOB_STATE_TABS: QueueJobState[] = [
  QueueJobState.Active,
  QueueJobState.Waiting,
  QueueJobState.Delayed,
  QueueJobState.Failed,
  QueueJobState.Paused,
  QueueJobState.Completed
]

const formatDate = (value?: string | null): string => {
  if (!value) return '—'
  const date = new Date(value)
  return isNaN(date.getTime()) ? '—' : date.toLocaleString()
}

const countChipColor = (
  key: string,
  value: number
): 'default' | 'error' | 'warning' | 'info' | 'success' => {
  if (value === 0) return 'default'
  if (key === 'failed') return 'error'
  if (key === 'active') return 'success'
  if (key === 'waiting' || key === 'delayed' || key === 'paused') return 'warning'
  return 'info'
}

interface QueueJobsDialogProps {
  queueName: string | null
  onClose: () => void
}

const QueueJobsDialog: FC<QueueJobsDialogProps> = ({ queueName, onClose }) => {
  const [stateTab, setStateTab] = useState<QueueJobState>(QueueJobState.Failed)
  const [page, setPage] = useState(0)
  const [jobToRemove, setJobToRemove] = useState<string | null>(null)
  const [selectedError, setSelectedError] = useState<{
    id: string
    failedReason?: string | null
    stacktrace?: string | null
  } | null>(null)

  const [fetchJobs, { data, loading, refetch }] = useQueueJobsLazyQuery({
    fetchPolicy: 'network-only'
  })

  const [retryJob, { loading: retrying }] = useRetryQueueJobMutation()
  const [removeJob, { loading: removing }] = useRemoveQueueJobMutation()

  const loadJobs = (state: QueueJobState, nextPage: number) => {
    if (!queueName) return
    fetchJobs({
      variables: {
        fetchQueueJobsInput: {
          queueName,
          state,
          start: nextPage * JOB_PAGE_SIZE,
          limit: JOB_PAGE_SIZE
        }
      }
    })
  }

  // Load the initial (Failed) tab whenever the dialog opens for a queue.
  useMemo(() => {
    if (queueName) {
      setStateTab(QueueJobState.Failed)
      setPage(0)
      loadJobs(QueueJobState.Failed, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueName])

  const handleTabChange = (state: QueueJobState) => {
    setStateTab(state)
    setPage(0)
    loadJobs(state, 0)
  }

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage)
    loadJobs(stateTab, nextPage)
  }

  const handleRetry = async (jobId: string) => {
    if (!queueName) return
    try {
      await retryJob({ variables: { queueJobActionInput: { queueName, jobId } } })
      Alert.success(`Job ${jobId} queued for retry`)
      await refetch?.()
    } catch (error) {
      Alert.error((error as Error).message)
    }
  }

  const handleRemove = async () => {
    if (!queueName || !jobToRemove) return
    try {
      await removeJob({ variables: { queueJobActionInput: { queueName, jobId: jobToRemove } } })
      Alert.success(`Job ${jobToRemove} removed`)
      setJobToRemove(null)
      await refetch?.()
    } catch (error) {
      Alert.error((error as Error).message)
      setJobToRemove(null)
    }
  }

  const jobs = data?.queueJobs?.jobs ?? []
  const total = data?.queueJobs?.total ?? 0

  return (
    <>
      <Dialog
        open={!!queueName}
        onClose={(_event, reason) => {
          // Only close via the explicit Close button — ignore backdrop clicks and Escape.
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') onClose()
        }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Jobs - {queueName}</Typography>
            <Tooltip title="Refresh">
              <IconButton onClick={() => loadJobs(stateTab, page)} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Tabs
            value={stateTab}
            onChange={(_, value) => handleTabChange(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2 }}
          >
            {JOB_STATE_TABS.map((state) => (
              <Tab key={state} label={state} value={state} sx={{ textTransform: 'capitalize' }} />
            ))}
          </Tabs>

          {stateTab === QueueJobState.Completed && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Completed jobs are automatically removed from Redis to free up memory, so this list is
              expected to be empty or to show only the most recent jobs.
            </Typography>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Attempts</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Finished</TableCell>
                    <TableCell>Error</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No {stateTab} jobs
                      </TableCell>
                    </TableRow>
                  ) : (
                    jobs.map((job) => (
                      <TableRow key={job.id} hover>
                        <TableCell>{job.id}</TableCell>
                        <TableCell>{job.name}</TableCell>
                        <TableCell align="right">{job.attemptsMade}</TableCell>
                        <TableCell>{formatDate(job.createdAt)}</TableCell>
                        <TableCell>{formatDate(job.finishedAt)}</TableCell>
                        <TableCell sx={{ maxWidth: 360 }}>
                          {job.failedReason ? (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Typography
                                variant="body2"
                                color="error"
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              >
                                {job.failedReason}
                              </Typography>
                              <Link
                                component="button"
                                type="button"
                                variant="body2"
                                underline="hover"
                                sx={{ flexShrink: 0 }}
                                onClick={() =>
                                  setSelectedError({
                                    id: job.id,
                                    failedReason: job.failedReason,
                                    stacktrace: job.stacktrace
                                  })
                                }
                              >
                                more
                              </Link>
                            </Stack>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {stateTab === QueueJobState.Failed && (
                              <Tooltip title="Retry job">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={retrying}
                                    onClick={() => handleRetry(job.id)}
                                  >
                                    <ReplayIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip title="Remove job">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={removing}
                                  onClick={() => setJobToRemove(job.id)}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {/* `page` state is 0-based (feeds `start`); PaginationWithJump is 1-based. */}
              <PaginationWithJump
                count={Math.ceil(total / JOB_PAGE_SIZE)}
                page={page + 1}
                onChange={nextPage => handlePageChange(nextPage - 1)}
                disabled={loading}
              />
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!selectedError}
        onClose={() => setSelectedError(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Error — Job {selectedError?.id}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>
            Reason
          </Typography>
          <Typography variant="body2" color="error" sx={{ whiteSpace: 'pre-wrap', mb: selectedError?.stacktrace ? 2 : 0 }}>
            {selectedError?.failedReason || '—'}
          </Typography>
          {selectedError?.stacktrace && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Stack trace
              </Typography>
              <Box
                component="pre"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: 12,
                  bgcolor: 'action.hover',
                  p: 1.5,
                  borderRadius: 1,
                  m: 0,
                  maxHeight: 360,
                  overflow: 'auto'
                }}
              >
                {selectedError.stacktrace}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setSelectedError(null)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        isOpen={!!jobToRemove}
        title="Remove job"
        message={`Remove job ${jobToRemove} from the "${queueName}" queue? This permanently deletes the job and cannot be undone.`}
        confirmText="Remove"
        variant="danger"
        isLoading={removing}
        onConfirm={handleRemove}
        onCancel={() => setJobToRemove(null)}
      />
    </>
  )
}

const QueueMonitor: FC = () => {
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [selectedQueue, setSelectedQueue] = useState<string | null>(null)
  const [queueToPause, setQueueToPause] = useState<string | null>(null)

  const { data, loading, refetch } = useQueuesOverviewQuery({
    fetchPolicy: 'network-only',
    pollInterval: autoRefresh ? REFRESH_INTERVAL_MS : 0
  })

  const [pauseQueue, { loading: pausing }] = usePauseQueueMutation()
  const [resumeQueue, { loading: resuming }] = useResumeQueueMutation()

  const queues = data?.queuesOverview?.queues ?? []

  const handlePause = async () => {
    if (!queueToPause) return
    try {
      await pauseQueue({ variables: { queueNameInput: { queueName: queueToPause } } })
      Alert.success(`Queue "${queueToPause}" paused`)
      setQueueToPause(null)
      await refetch()
    } catch (error) {
      Alert.error((error as Error).message)
      setQueueToPause(null)
    }
  }

  const handleResume = async (queueName: string) => {
    try {
      await resumeQueue({ variables: { queueNameInput: { queueName } } })
      Alert.success(`Queue "${queueName}" resumed`)
      await refetch()
    } catch (error) {
      Alert.error((error as Error).message)
    }
  }

  return (
    <Box p={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="h5">Background Jobs</Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <FormControlLabel
            control={<Switch checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />}
            label="Auto-refresh"
          />
          <Tooltip title="Refresh now">
            <IconButton onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Live view of the worker queues. Pausing a queue stops workers from picking up <strong>new</strong> jobs;
        jobs already running finish normally. Use this to let reports and print jobs drain when a large batch (e.g.
        monthly bill snapshots) is monopolising the workers.
      </Typography>

      {loading && queues.length === 0 ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          {queues.map((queue) => {
            const counts = queue.counts
            const countEntries: [string, number][] = [
              ['active', counts.active],
              ['waiting', counts.waiting],
              ['delayed', counts.delayed],
              ['failed', counts.failed],
              ['paused', counts.paused],
              ['completed', counts.completed]
            ]
            return (
              <Paper key={queue.name} variant="outlined" sx={{ p: 2 }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                      <Typography variant="h6">{queue.name}</Typography>
                      {queue.isPaused && <Chip label="PAUSED" color="warning" size="small" />}
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {countEntries.map(([key, value]) => (
                        <Chip
                          key={key}
                          label={`${key}: ${value}`}
                          size="small"
                          color={countChipColor(key, value)}
                          variant={value === 0 ? 'outlined' : 'filled'}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      ))}
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button variant="outlined" size="small" onClick={() => setSelectedQueue(queue.name)}>
                      View jobs
                    </Button>
                    {queue.isPaused ? (
                      <Button
                        variant="contained"
                        size="small"
                        color="success"
                        startIcon={<PlayCircleOutlineIcon />}
                        disabled={resuming}
                        onClick={() => handleResume(queue.name)}
                      >
                        Resume
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        startIcon={<PauseCircleOutlineIcon />}
                        disabled={pausing}
                        onClick={() => setQueueToPause(queue.name)}
                      >
                        Pause
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            )
          })}
        </Stack>
      )}

      <QueueJobsDialog queueName={selectedQueue} onClose={() => setSelectedQueue(null)} />

      <ConfirmationModal
        isOpen={!!queueToPause}
        title="Pause queue"
        message={`Pause the "${queueToPause}" queue? Workers will stop picking up new jobs until you resume it. Jobs already running will finish.${
          queueToPause === 'billing'
            ? '\n\nWarning: the billing queue handles bill sending, approvals, voids and snapshots — pause it only briefly during an incident.'
            : ''
        }`}
        confirmText="Pause"
        variant="danger"
        isLoading={pausing}
        onConfirm={handlePause}
        onCancel={() => setQueueToPause(null)}
      />
    </Box>
  )
}

export default QueueMonitor
